import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDsTranslate, useDsServices, useDsPermission } from '../context/DsProvider';
import type { TemplatePlaceholder, PlaceholderCatalogEntry } from '../types/template';
import styles from './DataPlaceholderPanel.module.scss';

type FieldTypeForIcon =
  | TemplatePlaceholder['fieldType']
  | PlaceholderCatalogEntry['fieldType'];

interface DataPlaceholderPanelProps {
  templateId?: string;
  docKey?: string;
  autoScanning?: boolean;
}

export function DataPlaceholderPanel({ templateId, docKey, autoScanning = false }: DataPlaceholderPanelProps) {
  const t = useDsTranslate();
  const queryClient = useQueryClient();
  const { template: templateService, placeholderCatalog: catalogService } = useDsServices();
  const hasPermission = useDsPermission();
  const [scanning, setScanning] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const DATA_SOURCE_OPTIONS = useMemo(() => [
    { value: 'TENANT', label: t('K_DS_SOURCE_TENANT', 'Client') },
    { value: 'LEGAL_ENTITY', label: t('K_DS_SOURCE_ENTITY', 'Legal Entity') },
    { value: 'USER', label: t('K_DS_SOURCE_USER', 'User') },
    { value: 'ORGANIZATION', label: t('K_DS_SOURCE_ORG', 'Company') },
    { value: 'CUSTOM', label: t('K_DS_SOURCE_CUSTOM', 'Custom (manual)') },
  ], [t]);

  const FIELD_TYPE_OPTIONS = useMemo(() => [
    { value: 'TEXT', label: t('K_DS_FIELD_TEXT', 'Text') },
    { value: 'DATE', label: t('K_DS_FIELD_DATE', 'Date') },
    { value: 'NUMBER', label: t('K_DS_FIELD_NUMBER', 'Number') },
    { value: 'EMAIL', label: t('K_DS_FIELD_EMAIL', 'Email') },
    { value: 'PHONE', label: t('K_DS_FIELD_PHONE', 'Phone') },
    { value: 'CURRENCY', label: t('K_DS_FIELD_CURRENCY', 'Currency') },
    { value: 'CHECKBOX', label: t('K_DS_FIELD_CHECKBOX', 'Checkbox') },
    { value: 'SIGNATURE', label: t('K_DS_FIELD_SIGNATURE', 'Signature') },
    { value: 'DROPDOWN', label: t('K_DS_FIELD_DROPDOWN', 'Dropdown') },
  ], [t]);

  // Catalog is session-authed only — no specific permission gate per BE contract
  // (the wizard needs it to render the copy-to-clipboard list).
  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['ds-placeholder-catalog'],
    queryFn: () => catalogService!.list(),
    enabled: !!catalogService,
    staleTime: Infinity,
  });

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (e) =>
        e.placeholderKey.toLowerCase().includes(q) ||
        e.displayLabel.toLowerCase().includes(q),
    );
  }, [catalog, catalogSearch]);

  const { data: placeholders = [] } = useQuery({
    queryKey: ['ds-template-placeholders', templateId],
    queryFn: () => templateService!.getDataPlaceholders(templateId!),
    enabled: !!templateId && !!templateService && hasPermission('CONTRACT_TEMPLATES_VIEW'),
  });

  const scanMutation = useMutation({
    mutationFn: async () => {
      if (!templateService) throw new Error('Template service not provided');
      if (docKey) {
        try {
          await templateService.forceSave(docKey);
        } catch {
          // Continue even if forcesave fails
        }
        // Wait for OnlyOffice callback to persist the saved document before scanning
        await new Promise(r => setTimeout(r, 3000));
      }
      return templateService.scanPlaceholders(templateId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ds-template-placeholders', templateId] });
      setScanning(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (updated: TemplatePlaceholder[]) =>
      templateService!.upsertDataPlaceholders(templateId!, updated),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ds-template-placeholders', templateId] });
    },
  });

  const handleFieldTypeChange = (key: string, newFieldType: string) => {
    const updated = placeholders.map((ph) =>
      ph.placeholderKey === key ? { ...ph, fieldType: newFieldType as TemplatePlaceholder['fieldType'] } : ph,
    );
    updateMutation.mutate(updated);
  };

  const handleCopy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`);
      setCopiedKey(key);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // Clipboard write can fail in non-secure contexts; surface nothing — user
      // can still type the token manually from the visible mono span.
    }
  };

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  function getFieldTypeIcon(fieldType?: FieldTypeForIcon): string {
    switch (fieldType) {
      case 'DATE': return '📅';
      case 'NUMBER': return '#';
      case 'EMAIL': return '@';
      case 'PHONE': return '📞';
      case 'CURRENCY': return '$';
      case 'AMOUNT': return '₹';
      case 'CHECKBOX': return '☑';
      case 'SIGNATURE': return '✍';
      case 'DROPDOWN': return '▾';
      default: return 'T';
    }
  }

  if (!templateId) {
    return (
      <div className={styles.panel}>
        <p className={styles.hint}>{t('K_DS_SAVE_FIRST', 'Save the template first to manage placeholders.')}</p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('K_DS_AVAILABLE_PLACEHOLDERS', 'Available placeholders')}
        </h3>
        <p className={styles.sectionHelp}>
          {t(
            'K_DS_AVAILABLE_PLACEHOLDERS_HELP',
            'Click copy, then paste the token into the document.',
          )}
        </p>
        <input
          type="search"
          className={styles.search}
          value={catalogSearch}
          onChange={(e) => setCatalogSearch(e.target.value)}
          placeholder={t('K_DS_SEARCH_PLACEHOLDERS', 'Search placeholders…')}
          aria-label={t('K_DS_SEARCH_PLACEHOLDERS', 'Search placeholders…')}
        />

        {catalogLoading ? (
          <p className={styles.hint}>{t('K_DS_LOADING_CATALOG', 'Loading available placeholders…')}</p>
        ) : filteredCatalog.length === 0 ? (
          <p className={styles.hint}>
            {catalogSearch
              ? t('K_DS_NO_AVAILABLE_PLACEHOLDERS', 'No placeholders match your search')
              : t('K_DS_NO_CATALOG_ENTRIES', 'No available placeholders configured')}
          </p>
        ) : (
          <div className={styles.list}>
            {filteredCatalog.map((entry) => {
              const justCopied = copiedKey === entry.placeholderKey;
              return (
                <div key={entry.id} className={styles.catalogItem}>
                  <div className={styles.catalogItemMain}>
                    <span className={styles.fieldTypeIcon} aria-hidden>
                      {getFieldTypeIcon(entry.fieldType)}
                    </span>
                    <div className={styles.catalogItemLabels}>
                      <span className={styles.catalogLabel}>{entry.displayLabel}</span>
                      <span className={styles.key}>{`{{${entry.placeholderKey}}}`}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={() => handleCopy(entry.placeholderKey)}
                      aria-label={t('K_DS_COPY_PLACEHOLDER', 'Copy placeholder')}
                      data-copied={justCopied || undefined}
                    >
                      {justCopied
                        ? t('K_DS_PLACEHOLDER_COPIED', 'Copied')
                        : t('K_DS_COPY_PLACEHOLDER', 'Copy')}
                    </button>
                  </div>
                  {entry.description && (
                    <p className={styles.description}>{entry.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('K_DS_DISCOVERED_PLACEHOLDERS', 'Discovered in document')}
        </h3>
        <button
          className={styles.scanBtn}
          onClick={() => {
            setScanning(true);
            scanMutation.mutate();
          }}
          disabled={scanning || autoScanning}
        >
          {scanning || autoScanning
            ? t('K_DS_SCANNING', 'Scanning...')
            : t('K_DS_SCAN_PLACEHOLDERS', 'Scan for {{placeholders}}')}
        </button>

        {placeholders.length === 0 ? (
          <p className={styles.hint}>
            {t(
              'K_DS_NO_PLACEHOLDERS',
              'No placeholders found in the document. Paste a {{token}} from above into your DOCX, then save.',
            )}
          </p>
        ) : (
          <div className={styles.list}>
            {placeholders.map((ph) => (
              <div key={ph.placeholderKey} className={styles.item}>
                <div className={styles.itemHeader}>
                  <span className={styles.fieldTypeIcon}>{getFieldTypeIcon(ph.fieldType)}</span>
                  <span className={styles.key}>{`{{${ph.placeholderKey}}}`}</span>
                </div>
                <div className={styles.itemControls}>
                  <select
                    className={styles.fieldTypeSelect}
                    value={ph.fieldType || 'TEXT'}
                    onChange={(e) => handleFieldTypeChange(ph.placeholderKey, e.target.value)}
                  >
                    {FIELD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className={styles.source}>
                    {DATA_SOURCE_OPTIONS.find((o) => o.value === ph.dataSourceType)?.label ?? ph.dataSourceType}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
